import { ThinkingLevel } from 'firebase/ai';
import recordPromptConfig from '../config/portfolioRecordAiConfig.json';
import {
  HTML_EDITOR_PARTIAL_MAX_OUTPUT_TOKENS,
  redactBase64FromAiPrompt,
  requestEditorJsonWithRetry,
} from './htmlAiRequest.js';
import { ICON_OPTIONS } from '../../../Common/Icons/iconOptions.js';

const PROJECT_FIELDS = new Set(['title', 'overview', 'skillIds', 'imageDescriptions']);
const SKILL_FIELDS = new Set(['title', 'description', 'icon']);
const ALLOWED_SKILL_ICONS = new Set(ICON_OPTIONS.map((option) => option.value));
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const UPDATE_SCOPES = new Set(['all', 'projects', 'skills']);

const text = (value, maxLength) => String(value ?? '').trim().slice(0, maxLength);
const safeObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);
const sameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const imageIdOf = (image) => text(image?.id ?? image?.imageId, 160);

const compactProjectContext = (projects = []) => projects.slice(0, 30).map((project) => ({
  id: text(project?.id, 160),
  title: text(project?.title, 240),
  overview: text(project?.overview, 3000),
  skillIds: Array.isArray(project?.skillsUsed) ? project.skillsUsed.map(String).slice(0, 40) : [],
  images: (Array.isArray(project?.images) ? project.images : []).slice(0, 30).map((image) => ({
    imageId: imageIdOf(image),
    description: text(image?.description, 160),
  })).filter((image) => image.imageId),
}));

const compactSkillContext = (skills = []) => skills.slice(0, 60).map((skill) => ({
  id: text(skill?.id, 160),
  title: text(skill?.name, 180),
  description: text(skill?.description, 2000),
  icon: skill?.icon?.type === 'bootstrap' ? text(skill.icon.value, 80) : null,
}));

export const buildPortfolioRecordUpdatePrompt = ({ prompt, projects = [], skills = [], scope = 'all' } = {}) => [
  recordPromptConfig.intro,
  scope === 'projects'
    ? 'Editing scope: Projects only. Return an empty skillUpdates array.'
    : scope === 'skills'
      ? 'Editing scope: Skills only. Return an empty projectUpdates array.'
      : 'Editing scope: Projects and skills.',
  `User request:\n${text(redactBase64FromAiPrompt(prompt), 2400)}`,
  `Existing projects:\n${JSON.stringify(compactProjectContext(projects))}`,
  `Existing skills:\n${JSON.stringify(compactSkillContext(skills))}`,
  `Approved skill icons:\n${JSON.stringify([...ALLOWED_SKILL_ICONS])}`,
  `Return exactly this shape:\n${JSON.stringify(recordPromptConfig.response, null, 2)}`,
  `Rules:\n${recordPromptConfig.rules.map((rule) => `- ${rule}`).join('\n')}`,
].join('\n\n');

const normalizeProjectChanges = (changes, project, allowedSkillIds) => {
  const source = safeObject(changes);
  const normalized = {};
  Object.entries(source).forEach(([key, value]) => {
    if (UNSAFE_KEYS.has(key) || !PROJECT_FIELDS.has(key)) return;
    if (key === 'title') {
      const title = text(value, 240);
      if (title) normalized.title = title;
    }
    if (key === 'overview') {
      const overview = text(value, 6000);
      if (overview) normalized.overview = overview;
    }
    if (key === 'skillIds' && Array.isArray(value)) {
      normalized.skillIds = [...new Set(
        value.map(String).filter((id) => allowedSkillIds.has(id))
      )].slice(0, 40);
    }
    if (key === 'imageDescriptions' && Array.isArray(value)) {
      const currentImages = new Map(
        (Array.isArray(project?.images) ? project.images : [])
          .map((image) => [imageIdOf(image), image])
          .filter(([id]) => id)
      );
      const seenImages = new Set();
      normalized.imageDescriptions = value.slice(0, 30).flatMap((entry) => {
        const imageId = text(entry?.imageId, 160);
        if (!imageId || seenImages.has(imageId) || !currentImages.has(imageId)) return [];
        seenImages.add(imageId);
        const description = text(entry?.description, 160);
        return description === text(currentImages.get(imageId)?.description, 160)
          ? []
          : [{ imageId, description }];
      });
    }
  });
  return Object.fromEntries(Object.entries(normalized).filter(
    ([key, value]) => {
      if (key === 'skillIds') return !sameValue(value, project?.skillsUsed || []);
      if (key === 'imageDescriptions') return value.length > 0;
      return !sameValue(value, project?.[key]);
    }
  ));
};

const normalizeSkillChanges = (changes, skill) => {
  const source = safeObject(changes);
  const normalized = {};
  Object.entries(source).forEach(([key, value]) => {
    if (UNSAFE_KEYS.has(key) || !SKILL_FIELDS.has(key)) return;
    if (key === 'title') {
      const title = text(value, 180);
      if (title) normalized.title = title;
    }
    if (key === 'description') {
      const description = text(value, 3000);
      if (description) normalized.description = description;
    }
    if (key === 'icon') {
      if (value === null) normalized.icon = null;
      else {
        const icon = text(value, 80);
        if (ALLOWED_SKILL_ICONS.has(icon)) normalized.icon = icon;
      }
    }
  });
  return Object.fromEntries(Object.entries(normalized).filter(([key, value]) => {
    if (key === 'title') return value !== skill?.name;
    if (key === 'icon') {
      const currentIcon = skill?.icon?.type === 'bootstrap' ? skill.icon.value : null;
      return value !== currentIcon;
    }
    return value !== skill?.[key];
  }));
};

export const validatePortfolioRecordUpdates = (
  candidate,
  { projects = [], skills = [] } = {}
) => {
  const projectMap = new Map(projects.map((project) => [String(project?.id || ''), project]));
  const skillMap = new Map(skills.map((skill) => [String(skill?.id || ''), skill]));
  const allowedSkillIds = new Set(skills.map((skill) => String(skill?.id || '')).filter(Boolean));
  const seenProjects = new Set();
  const seenSkills = new Set();

  const projectUpdates = (Array.isArray(candidate?.projectUpdates) ? candidate.projectUpdates : [])
    .slice(0, 20)
    .flatMap((update) => {
      const id = text(update?.id, 160);
      if (!id || seenProjects.has(id) || !projectMap.has(id)) return [];
      seenProjects.add(id);
      const changes = normalizeProjectChanges(update.changes, projectMap.get(id), allowedSkillIds);
      return Object.keys(changes).length ? [{ id, changes }] : [];
    });

  const skillUpdates = (Array.isArray(candidate?.skillUpdates) ? candidate.skillUpdates : [])
    .slice(0, 30)
    .flatMap((update) => {
      const id = text(update?.id, 160);
      if (!id || seenSkills.has(id) || !skillMap.has(id)) return [];
      seenSkills.add(id);
      const changes = normalizeSkillChanges(update.changes, skillMap.get(id));
      return Object.keys(changes).length ? [{ id, changes }] : [];
    });

  return { projectUpdates, skillUpdates };
};

export const generatePortfolioRecordUpdates = async ({
  prompt,
  projects = [],
  skills = [],
  scope = 'all',
  onRetry,
} = {}) => {
  const safeScope = UPDATE_SCOPES.has(scope) ? scope : 'all';
  const generated = await requestEditorJsonWithRetry({
    buildPrompt: (retry) => [
      buildPortfolioRecordUpdatePrompt({ prompt, projects, skills, scope: safeScope }),
      retry ? 'The previous response was invalid. Return fewer updates and fully close the JSON.' : '',
    ].filter(Boolean).join('\n\n'),
    maxOutputTokens: Math.min(4096, HTML_EDITOR_PARTIAL_MAX_OUTPUT_TOKENS),
    generationConfig: { thinkingConfig: { thinkingLevel: ThinkingLevel.LOW } },
    fallbackGenerationConfig: { thinkingConfig: { thinkingLevel: ThinkingLevel.MINIMAL } },
    onRetry,
  });
  const validated = validatePortfolioRecordUpdates(generated, { projects, skills });
  return {
    projectUpdates: safeScope === 'skills' ? [] : validated.projectUpdates,
    skillUpdates: safeScope === 'projects' ? [] : validated.skillUpdates,
  };
};

export const countPortfolioRecordUpdates = (updates = {}) => (
  (updates.projectUpdates?.length || 0) + (updates.skillUpdates?.length || 0)
);

export const describePortfolioRecordUpdates = (updates = {}, { projects = [], skills = [] } = {}) => {
  const projectNames = new Map(projects.map((project) => [String(project.id), project.title || 'project']));
  const skillNames = new Map(skills.map((skill) => [String(skill.id), skill.name || 'skill']));
  const fieldLabels = {
    title: 'title',
    overview: 'overview',
    skillIds: 'linked skills',
    imageDescriptions: 'image descriptions',
  };
  return [
    ...(updates.projectUpdates || []).map((update) => (
      `Update project "${projectNames.get(update.id) || 'Project'}": ${Object.keys(update.changes)
      .map((field) => fieldLabels[field])
      .filter(Boolean)
      .join(', ')}.`
    )),
    ...(updates.skillUpdates || []).map((update) => (
      `Update skill "${skillNames.get(update.id) || 'Skill'}": ${Object.keys(update.changes)
        .map((field) => ({ title: 'title', description: 'description', icon: 'icon' }[field]))
        .filter(Boolean)
        .join(', ')}.`
    )),
  ];
};

const buildProjectFirebasePayload = (project, changes) => {
  const payload = {};
  if (Object.prototype.hasOwnProperty.call(changes, 'title')) payload.title = changes.title;
  if (Object.prototype.hasOwnProperty.call(changes, 'overview')) payload.overview = changes.overview;
  if (Object.prototype.hasOwnProperty.call(changes, 'skillIds')) payload.skillsUsed = [...changes.skillIds];
  if (changes.imageDescriptions?.length) {
    const descriptions = new Map(
      changes.imageDescriptions.map((entry) => [entry.imageId, entry.description])
    );
    payload.images = (Array.isArray(project?.images) ? project.images : []).map((image) => {
      const imageId = imageIdOf(image);
      return descriptions.has(imageId)
        ? { ...image, description: descriptions.get(imageId) }
        : image;
    });
  }
  return payload;
};

const buildSkillFirebasePayload = (changes) => {
  const payload = {};
  if (Object.prototype.hasOwnProperty.call(changes, 'title')) payload.name = changes.title;
  if (Object.prototype.hasOwnProperty.call(changes, 'description')) {
    payload.description = changes.description;
  }
  if (Object.prototype.hasOwnProperty.call(changes, 'icon')) {
    payload.icon = changes.icon ? { type: 'bootstrap', value: changes.icon } : null;
  }
  return payload;
};

export const applyPortfolioRecordUpdates = async ({
  userId,
  updates,
  projects = [],
  skills = [],
  updateProject,
  updateSkill,
} = {}) => {
  if (!userId) throw new Error('Sign in before updating saved projects or skills.');
  const validated = validatePortfolioRecordUpdates(updates, { projects, skills });
  if (validated.projectUpdates.length && typeof updateProject !== 'function') {
    throw new Error('Project updates are unavailable right now.');
  }
  if (validated.skillUpdates.length && typeof updateSkill !== 'function') {
    throw new Error('Skill updates are unavailable right now.');
  }
  const projectMap = new Map(projects.map((project) => [String(project?.id || ''), project]));
  for (const update of validated.projectUpdates) {
    const payload = buildProjectFirebasePayload(projectMap.get(update.id), update.changes);
    await updateProject(userId, update.id, payload);
  }
  for (const update of validated.skillUpdates) {
    await updateSkill(userId, update.id, buildSkillFirebasePayload(update.changes));
  }
  return validated;
};
